import React, { Component, ErrorInfo, ReactNode } from 'react';

// ============================================================
// Error Boundary - React Error Boundary Component
// ============================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error details
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Auto-recover from stale-chunk errors (occurs after a deploy when
    // the cached index.html references a now-missing /assets/<hash>.js file).
    // Do it at most once per session to avoid infinite reload loops.
    const msg = String(error?.message || '');
    const isStaleChunk =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk");

    if (isStaleChunk) {
      const marker = '__stale_chunk_reload_at';
      const last = Number(sessionStorage.getItem(marker) || 0);
      const now = Date.now();
      // Reload only once per 30s so we don't loop if the chunk is truly gone
      if (now - last > 30_000) {
        sessionStorage.setItem(marker, String(now));
        console.warn('[ErrorBoundary] Stale chunk detected — forcing hard reload.');
        // Bust any HTTP cache by appending a cache-buster query param
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', String(now));
        window.location.replace(url.toString());
        return;
      }
    }

    // Could send to error tracking service here
    try {
      const errorLog = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };
      console.error('[ErrorBoundary] Error log:', JSON.stringify(errorLog, null, 2));
    } catch {
      // Ignore logging errors
    }
  }

  handleReload = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleFullReload = (): void => {
    window.location.reload();
  };

  handleReport = (): void => {
    const { error, errorInfo } = this.state;
    const subject = encodeURIComponent(`[BotBoss Bug] ${error?.message || 'Unknown error'}`);
    const body = encodeURIComponent(
      `--- Bug Report ---\n\n` +
      `Error: ${error?.message}\n\n` +
      `Stack:\n${error?.stack?.slice(0, 500)}\n\n` +
      `Component:\n${errorInfo?.componentStack?.slice(0, 500)}\n\n` +
      `URL: ${window.location.href}\n` +
      `Time: ${new Date().toISOString()}\n`
    );
    window.open(
      `https://github.com/user/botboss/issues/new?title=${subject}&body=${body}`,
      '_blank'
    );
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>!</div>
            <h2 style={styles.title}>
              {'\u05DE\u05E9\u05D4\u05D5 \u05D4\u05E9\u05EA\u05D1\u05E9'}
            </h2>
            <p style={styles.subtitle}>
              {'\u05D0\u05D9\u05E8\u05E2\u05D4 \u05E9\u05D2\u05D9\u05D0\u05D4 \u05DC\u05D0 \u05E6\u05E4\u05D5\u05D9\u05D4. \u05D0\u05E0\u05D7\u05E0\u05D5 \u05DE\u05EA\u05E0\u05E6\u05DC\u05D9\u05DD.'}
            </p>

            {this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>
                  {'\u05E4\u05E8\u05D8\u05D9 \u05E9\u05D2\u05D9\u05D0\u05D4'}
                </summary>
                <pre style={styles.errorText}>
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack?.slice(0, 800)}
                </pre>
              </details>
            )}

            <div style={styles.actions}>
              <button onClick={this.handleReload} style={styles.primaryButton}>
                {'\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1'}
              </button>
              <button onClick={this.handleFullReload} style={styles.secondaryButton}>
                {'\u05E8\u05E2\u05E0\u05D5\u05DF \u05D3\u05E3'}
              </button>
              <button onClick={this.handleReport} style={styles.linkButton}>
                {'\u05D3\u05D5\u05D5\u05D7 \u05E2\u05DC \u05D1\u05D0\u05D2'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================
// Inline styles (dark mode + RTL)
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'var(--bg-primary, #0f1117)',
    direction: 'rtl',
    padding: '20px',
  },
  card: {
    background: 'var(--bg-secondary, #1a1d27)',
    border: '1px solid var(--border, #2d3245)',
    borderRadius: 'var(--radius-lg, 16px)',
    padding: '48px 40px',
    maxWidth: '520px',
    width: '100%',
    textAlign: 'center',
    boxShadow: 'var(--shadow, 0 2px 8px rgba(0,0,0,0.3))',
  },
  icon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'rgba(239, 68, 68, 0.15)',
    color: 'var(--error, #ef4444)',
    fontSize: '32px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  title: {
    color: 'var(--text-primary, #e4e7ef)',
    fontSize: '22px',
    fontWeight: 600,
    margin: '0 0 8px',
    fontFamily: 'var(--font-sans)',
  },
  subtitle: {
    color: 'var(--text-secondary, #9ca3b4)',
    fontSize: '15px',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  details: {
    textAlign: 'left' as const,
    direction: 'ltr' as const,
    marginBottom: '24px',
    background: 'var(--bg-tertiary, #242836)',
    borderRadius: 'var(--radius-md, 10px)',
    padding: '12px 16px',
    border: '1px solid var(--border, #2d3245)',
  },
  summary: {
    color: 'var(--text-secondary, #9ca3b4)',
    cursor: 'pointer',
    fontSize: '13px',
    userSelect: 'none' as const,
  },
  errorText: {
    color: 'var(--error, #ef4444)',
    fontSize: '12px',
    fontFamily: 'var(--font-mono)',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    marginTop: '8px',
    maxHeight: '200px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
  },
  primaryButton: {
    padding: '10px 24px',
    borderRadius: 'var(--radius-md, 10px)',
    border: 'none',
    background: 'var(--accent, #6366f1)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  secondaryButton: {
    padding: '10px 24px',
    borderRadius: 'var(--radius-md, 10px)',
    border: '1px solid var(--border, #2d3245)',
    background: 'transparent',
    color: 'var(--text-secondary, #9ca3b4)',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  linkButton: {
    padding: '10px 16px',
    borderRadius: 'var(--radius-md, 10px)',
    border: 'none',
    background: 'transparent',
    color: 'var(--accent, #6366f1)',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export default ErrorBoundary;
